function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext, useRef, useEffect, useState } from 'react';
import cn from 'clsx';
import { arrayOf, bool, string, shape } from 'prop-types';
import { useTranslation } from 'react-i18next';
import Image from '../../atoms/Image/index.jsx';
import List from '../List/index.jsx';
import MenuLink from '../../atoms/MenuLink/index.jsx';
import BackgroundImage from '../../atoms/BackgroundImage/index.jsx';
import PopupToggleButton from '../PopupToggleButton/index.jsx';
import AccountNav from '../AccountNav/index.jsx';
import PopupContext from '../../context/PopupContext.js';
import { debounce } from '../../utils/index.js';
import { useDesignTokens } from '../../hooks/index.js';

/**
 * Eyebrow component.
 */
const EyebrowNav = ({
  className,
  items,
  logo,
  logoUrl,
  swapContent,
  accountNavOptions,
  avatarImage,
  mobileBackgroundImage
}) => {
  const {
    activePopup,
    popupKeys
  } = useContext(PopupContext);
  const [showBackgroundImage, setShowBackgroundImage] = useState(false);
  const containerRef = useRef(null);
  const {
    grid
  } = useDesignTokens();
  const {
    t
  } = useTranslation();
  const handleResize = debounce(() => {
    // Detect if window size is less than or equal to our desktop breakpoint
    setShowBackgroundImage(window.innerWidth <= (grid === null || grid === void 0 ? void 0 : grid.lg));
  }, 100);

  // Check if the image is show on first render
  useEffect(() => {
    const {
      offsetWidth
    } = containerRef.current;
    setShowBackgroundImage(offsetWidth <= (grid === null || grid === void 0 ? void 0 : grid.lg));
  }, []);

  // Watch for resizing of the viewport to show or hide the image
  useEffect(() => {
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, [containerRef]);
  if (avatarImage) {
    avatarImage.sizes = ['28px'];
    avatarImage.srcSetBreakpoints = [28, 56, 84];
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cn(className, 'eyebrow-nav'),
    ref: containerRef
  }, /*#__PURE__*/React.createElement(BackgroundImage, {
    image: showBackgroundImage ? mobileBackgroundImage : null
  }, /*#__PURE__*/React.createElement("div", {
    className: "eyebrow-nav__container container container--no-gutters"
  }, popupKeys.mainNav && /*#__PURE__*/React.createElement("div", {
    className: 'eyebrow-nav__icon eyebrow-nav__icon--muted mr-xs' + (activePopup === popupKeys.mainNav ? " eyebrow-nav__icon--open" : "")
  }, /*#__PURE__*/React.createElement(PopupToggleButton, {
    popup: popupKeys.mainNav,
    openIcon: "menu",
    closeIcon: "menu-close"
  })), logo && /*#__PURE__*/React.createElement(Image, _extends({
    className: "eyebrow-nav__logo eyebrow-nav__brand-logo",
    maxHeight: 24,
    maxWidth: 120,
    fluid: true
  }, logo, {
    sizes: ['120px'],
    srcSetBreakpoints: [120, 240, 360, 480 // Up to 4x the max width for more pixel dense devices
    ],

    link: {
      href: logoUrl,
      rel: 'home'
    },
    loading: "eager"
  })), /*#__PURE__*/React.createElement("div", {
    className: "eyebrow-nav__desktop"
  }, swapContent && /*#__PURE__*/React.createElement("div", {
    className: "mt-xxs mb-xxs"
  }, /*#__PURE__*/React.createElement(Image, _extends({
    className: "eyebrow-nav__logo",
    maxHeight: 25,
    maxWidth: 120,
    fluid: true
  }, logo, {
    srcSetBreakpoints: [120, 240, 360, 480],
    sizes: ['120px'],
    link: {
      href: logoUrl,
      rel: 'home'
    },
    loading: "eager"
  }))), !swapContent && items.length > 0 && /*#__PURE__*/React.createElement("nav", {
    className: "eyebrow-nav__navigation",
    "aria-label": t('molecules.eyebrowNav.ariaLabel.quickLinks'),
    "data-feature": "QuickLinks"
  }, /*#__PURE__*/React.createElement(List, {
    horizontal: true
  }, items.map((item, index) => /*#__PURE__*/React.createElement(List.Item, {
    key: `${item.label}-${item.relativeUrl || item.absoluteUrl}`,
    className: "eyebrow-nav__navigation-listitem body-copy-small",
    "data-index": index + 1,
    "data-component": "MenuLink",
    "data-item-name": item.label
  }, /*#__PURE__*/React.createElement(MenuLink, _extends({
    className: "eyebrow-nav__navigation-link"
  }, Object.assign(item, {
    children: null
  })), item.label)))))), accountNavOptions && /*#__PURE__*/React.createElement("div", {
    className: "eyebrow-nav__sign-in hidden-for-md-down"
  }, /*#__PURE__*/React.createElement(AccountNav, _extends({}, accountNavOptions, {
    avatarImage: avatarImage
  }))), /*#__PURE__*/React.createElement("div", {
    className: "eyebrow-nav__items-right ml-xs"
  }, popupKeys.account && accountNavOptions && /*#__PURE__*/React.createElement("div", {
    className: "eyebrow-nav__icon eyebrow-nav__account-icon" + (activePopup === popupKeys.account ? " eyebrow-nav__icon--open" : "")
  }, /*#__PURE__*/React.createElement(PopupToggleButton, {
    popup: popupKeys.account,
    openIcon: "profile",
    closeIcon: "cross"
  })), popupKeys.search && /*#__PURE__*/React.createElement("div", {
    className: "eyebrow-nav__icon eyebrow-nav__search-icon" + (activePopup === popupKeys.search ? " eyebrow-nav__icon--open" : "")
  }, /*#__PURE__*/React.createElement(PopupToggleButton, {
    popup: popupKeys.search,
    openIcon: "search",
    closeIcon: "cross"
  }))))));
};
EyebrowNav.defaultProps = {
  className: null,
  logo: null,
  items: [],
  swapContent: false,
  logoUrl: '/',
  accountNavOptions: null,
  avatarImage: null,
  mobileBackgroundImage: null
};
EyebrowNav.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** The brand logo */
  logo: shape(Image.propTypes),
  /** Array of navigation items */
  items: arrayOf(shape(MenuLink.propTypes)),
  /** Set via parent element and is when the nav is stuck to top of page */
  swapContent: bool,
  /** The URL to link to from the logo. */
  logoUrl: string,
  /** Account Navigation options */
  accountNavOptions: shape(AccountNav.propTypes),
  /** Avatar image props */
  avatarImage: shape(AccountNav.propTypes.Image),
  /** Optional background image which appears on mobile only */
  mobileBackgroundImage: string
} : {};
export default EyebrowNav;